/**
 * inventoryFilterUrl — serialize / deserialize the filters[] array to
 * URL search params for shareable links and refresh-safe state.
 *
 * Format: ?f=axis:value,axis:value,axis:value
 *   - Comma-separated chips
 *   - Each chip is "<axisKey>:<value>"
 *   - Multi-value within an axis = repeated axis entries
 *
 * Values may contain ':' so we split on the first ':' only. URL-encoded
 * via URLSearchParams.set/get so commas/quotes/etc are safe.
 */

export function filtersToParam(filters) {
  if (!filters || filters.length === 0) return '';
  return filters.map(f => `${f.axis}:${f.value}`).join(',');
}

export function paramToFilters(param) {
  if (!param) return [];
  return param.split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => {
      const i = s.indexOf(':');
      if (i <= 0) return null;
      const axis = s.slice(0, i);
      const value = s.slice(i + 1);
      if (!axis || !value) return null;
      return { axis, value };
    })
    .filter(Boolean);
}

/** Read filters[] from window.location.search. */
export function readFiltersFromUrl() {
  if (typeof window === 'undefined') return [];
  const params = new URLSearchParams(window.location.search);
  return paramToFilters(params.get('f'));
}

/** Write filters[] to window.location (via history.replaceState — no reload). */
export function writeFiltersToUrl(filters) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const serialized = filtersToParam(filters);
  if (serialized) params.set('f', serialized);
  else params.delete('f');
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}

// ── Columns persistence (?cols=name,size,status,date) ────────────
// Omitted from the URL when the visible set matches the default — keeps
// shared links clean for the common case.

export function columnsToParam(columns) {
  if (!Array.isArray(columns) || columns.length === 0) return '';
  return columns.join(',');
}

export function paramToColumns(param) {
  if (!param) return null;
  const ids = param.split(',').map(s => s.trim()).filter(Boolean);
  return ids.length > 0 ? ids : null;
}

/** Read columns from window.location.search. Returns null when absent
 *  (signalling "no override — use saved prefs or defaults"). */
export function readColumnsFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return paramToColumns(params.get('cols'));
}

/** Write columns[] to the URL. Caller decides whether the set is "default"
 *  and should be omitted — we just serialize. */
export function writeColumnsToUrl(columns, { omit = false } = {}) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (omit) {
    params.delete('cols');
  } else {
    const serialized = columnsToParam(columns);
    if (serialized) params.set('cols', serialized);
    else params.delete('cols');
  }
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}

// ── Unit persistence (?unit=files|documents) ─────────────────────
// Written only when the user chose a unit explicitly; absent = automatic
// (documents when the case has a cut file). Deep links — e.g. from Ask
// Arc — carry it so the reader lands in the right unit.

export function readUnitFromUrl() {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('unit');
  return v === 'files' || v === 'documents' ? v : null;
}

export function writeUnitToUrl(unit) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (unit === 'files' || unit === 'documents') params.set('unit', unit);
  else params.delete('unit');
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}
